import {
  Button,
  DatePicker,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Textarea,
  TOUCH,
} from "@nymbl/nos2-rules";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { TaskOption, TaskSearch } from "@/components/time/TaskSearch";
import { TypeChip } from "@/components/time/TypeChip";
import { useTaskAssignments } from "@/hooks/use-time-assignable";
import {
  invalidateAfterEntryWrite,
  RULE_REFUSAL,
  useCreateEntry,
  useUpdateEntry,
} from "@/hooks/use-time-entries";
import { useTimeWeek } from "@/hooks/use-time-week";
import { ApiError } from "@/lib/time/api-client";
import { QUICK_HOURS } from "@/lib/time/constants";
import { type IsoDate, weekStartOf } from "@/lib/time/dates";
import { copyLinkLabel, projectLine } from "@/lib/time/labels";
import {
  checkLogTime,
  findAssignableFor,
  isSameTask,
  logTimeContext,
  quickHoursLabel,
} from "@/lib/time/log-time";
import { newId } from "@/lib/time/new-id";
import { formatHours, normalizeComment } from "@/lib/time/rules";
import {
  copyTaskLink,
  INVALID_LINK_TEXT,
  NOT_ASSIGNED_TEXT,
  resolveTaskLink,
} from "@/lib/time/task-link";
import { cn } from "@/lib/utils";
import type { AssignableTask, TimeEntryView } from "@/schemas/time";

export type LogTimeMode =
  | { kind: "new" }
  | { kind: "task"; task: AssignableTask }
  /** A shared task link (`?log=open&task=`): null when the link's task id is malformed. */
  | { kind: "link"; projectTaskId: string | null }
  | { kind: "copy"; entry: TimeEntryView }
  | { kind: "edit"; entry: TimeEntryView };

interface LogTimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The parent remounts the dialog with a new `key` per opening so the form starts fresh. */
  mode: LogTimeMode;
  defaultDate: IsoDate;
  today: IsoDate;
}

/** The answer to a write that may have saved; the hook already showed its toast. */
const MAY_HAVE_SAVED = 504;

/** "Use the copied or edited entry's own task", resolved from that entry's week. */
const FROM_SOURCE = "source";

/** "Use the linked task", resolved to the person's own assignment to it. */
const FROM_LINK = "link";

const MESSAGES_ID = "log-messages";

export function LogTimeDialog({
  open,
  onOpenChange,
  mode,
  defaultDate,
  today,
}: LogTimeDialogProps) {
  const source = mode.kind === "copy" || mode.kind === "edit" ? mode.entry : null;
  const editing = mode.kind === "edit" ? mode.entry : null;

  const [chosen, setChosen] = useState<
    AssignableTask | null | typeof FROM_SOURCE | typeof FROM_LINK
  >(
    mode.kind === "task"
      ? mode.task
      : source
        ? FROM_SOURCE
        : mode.kind === "link"
          ? FROM_LINK
          : null,
  );
  // A copied entry whose task can no longer be logged to opens the search on its name.
  const [query, setQuery] = useState(mode.kind === "copy" ? mode.entry.taskName : "");
  const [hours, setHours] = useState(source ? formatHours(source.hours) : "");
  const [date, setDate] = useState<IsoDate>(source ? source.date : defaultDate);
  const [comment, setComment] = useState(source?.comment ?? "");
  // One id per new entry until its create succeeds, so a retry after "may have saved" hits
  // the primary key (the server answers "Already saved") instead of adding a copy.
  const [entryId, setEntryId] = useState(newId);
  // A rule refusal from the server, shown inline for the values it was given.
  const [refusal, setRefusal] = useState<{ text: string; for: string } | null>(null);

  // The source entry's week lists the task it was logged to (WeekView.assignable).
  const sourceWeek = useTimeWeek(weekStartOf(source?.date ?? defaultDate), {
    enabled: open && source !== null,
  });
  const sourceTask =
    source && sourceWeek.data ? findAssignableFor(source, sourceWeek.data.assignable) : null;
  // A task link: one assignment is picked as if chosen in the search; several are offered.
  const linkedTaskId = mode.kind === "link" ? mode.projectTaskId : null;
  const linked = useTaskAssignments(linkedTaskId, open && chosen === FROM_LINK);
  const link = linked.data ? resolveTaskLink(linked.data.items) : null;
  const resolving =
    (chosen === FROM_SOURCE && sourceWeek.isPending) ||
    (chosen === FROM_LINK && linkedTaskId !== null && linked.isPending);
  const task =
    chosen === FROM_SOURCE
      ? sourceTask
      : chosen === FROM_LINK
        ? link?.kind === "one"
          ? link.task
          : null
        : chosen;
  // An edit shows its own task (even one no longer open to new time) until Change is pressed.
  const showForm = editing !== null ? !resolving && chosen !== null : task !== null;
  // The task the edit moves the entry to, when one other than its own was picked.
  const movedTo =
    editing !== null && task !== null && chosen !== FROM_SOURCE && !isSameTask(editing, task)
      ? task
      : null;

  // The chosen date's week holds that month's plan and lock, so another month checks itself.
  const week = useTimeWeek(weekStartOf(date), { enabled: open && showForm });
  const queryClient = useQueryClient();
  // Rule refusals show inline here, so the hooks do not toast them as well.
  const create = useCreateEntry({ quietRefusals: true });
  const update = useUpdateEntry({ quietRefusals: true });
  const saving = create.isPending || update.isPending;

  const ctx = week.data ? logTimeContext({ task, date, week: week.data, editing }) : null;
  const check = checkLogTime({ ctx, raw: hours, date, comment, today });
  const commentMissing = check.commentRequired && normalizeComment(comment) === null;
  const valuesKey = `${task?.key ?? ""}|${hours}|${date}|${comment}`;
  const refusalText = refusal?.for === valuesKey ? refusal.text : null;
  const canSave = check.canSave && !saving && refusalText === null;

  async function save(again: boolean) {
    if (!canSave) return;
    const cleanComment = normalizeComment(comment);
    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          date,
          hours: check.hours,
          comment: cleanComment,
          openedChangedOn: editing.changedOn,
          ...(movedTo
            ? { projectTaskId: movedTo.projectTaskId, demandPersonId: movedTo.demandPersonId }
            : {}),
        });
        onOpenChange(false);
        return;
      }
      if (!task) return;
      await create.mutateAsync({
        id: entryId,
        projectTaskId: task.projectTaskId,
        demandPersonId: task.demandPersonId,
        date,
        hours: check.hours,
        comment: cleanComment,
      });
      setEntryId(newId());
      if (!again) {
        onOpenChange(false);
        return;
      }
      setHours("");
      setComment("");
    } catch (err) {
      if (err instanceof ApiError && err.status === MAY_HAVE_SAVED) {
        // Never resent on its own. Re-read so the grid and Entries show what the warehouse
        // holds. A new entry keeps its id, so pressing Save again cannot add a copy; an edit
        // closes, since its opened version may now be stale.
        try {
          await invalidateAfterEntryWrite(queryClient);
        } catch (refreshError) {
          console.error("Could not refresh time after a write that may have saved", refreshError);
        }
        if (editing) {
          toast.warning("This may have saved. Check the entry.");
          onOpenChange(false);
        }
        return;
      }
      if (err instanceof ApiError && err.status === RULE_REFUSAL) {
        setRefusal({ text: err.message, for: valuesKey });
      }
      // Anything else was already a toast.
    }
  }

  const taskName = task?.taskName ?? editing?.taskName ?? "";
  const projectName = projectLine(task?.projectName ?? editing?.projectName, task?.accountName);
  const type = task?.type ?? editing?.type ?? "internal";
  const loadError = (editing && sourceWeek.isError ? sourceWeek.error : null) ?? week.error;
  const messages = refusalText
    ? [...check.messages, { code: "refused", text: refusalText, tone: "danger" as const }]
    : check.messages;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      dirty={
        hours !== (source ? formatHours(source.hours) : "") ||
        comment !== (source?.comment ?? "") ||
        (editing !== null && date !== editing.date)
      }
    >
      <DialogContent size="lg">
        <DialogHeader className="text-left">
          <DialogTitle>{editing ? "Edit time" : "Log time"}</DialogTitle>
          <DialogDescription className="sr-only">
            Pick a task, then hours, date and comment.
          </DialogDescription>
        </DialogHeader>

        {!showForm && (
          <DialogBody className="space-y-3">
            {resolving && (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-11 w-full" />
              </div>
            )}

            {!resolving && !showForm && chosen === FROM_LINK && link?.kind === "several" && (
              // The same task through several demands: the person picks which one the time goes to.
              <fieldset className="min-w-0 space-y-2">
                <legend className="mb-2 text-sm font-semibold">Which assignment?</legend>
                <div className="divide-y rounded-md border">
                  {link.tasks.map((t) => (
                    <TaskOption key={t.key} task={t} showDemand onPick={setChosen} />
                  ))}
                </div>
              </fieldset>
            )}

            {!resolving && !showForm && !(chosen === FROM_LINK && link?.kind === "several") && (
              <div className="space-y-3">
                {chosen === FROM_LINK && (
                  <p
                    role="alert"
                    className="rounded-md bg-warning-bg px-3 py-2 text-sm text-warning"
                  >
                    {linkedTaskId === null
                      ? INVALID_LINK_TEXT
                      : linked.isError
                        ? linked.error.message
                        : NOT_ASSIGNED_TEXT}
                  </p>
                )}
                <TaskSearch query={query} onQueryChange={setQuery} onPick={setChosen} />
                {editing && (
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(TOUCH, "w-full")}
                    onClick={() => setChosen(FROM_SOURCE)}
                  >
                    Keep {editing.taskName}
                  </Button>
                )}
              </div>
            )}
          </DialogBody>
        )}

        {showForm && (
          <>
            <DialogBody className="space-y-5">
              <div className="flex items-start justify-between gap-3 rounded-lg bg-muted p-3">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{taskName}</span>
                    <TypeChip type={type} />
                  </div>
                  <div className="text-xs text-muted-foreground">{projectName}</div>
                  {ctx?.guidance ? (
                    <div className="text-xs text-muted-foreground">
                      {formatHours(ctx.monthLogged)} of {formatHours(ctx.guidance)} hrs
                    </div>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-1">
                  {task && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={TOUCH}
                      aria-label={copyLinkLabel(task)}
                      title="Copy a link that opens Log time on this task"
                      onClick={() => void copyTaskLink(task.projectTaskId)}
                    >
                      <Link className="size-4" aria-hidden="true" />
                      Copy link
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={TOUCH}
                    aria-label={`Change task (now ${taskName})`}
                    onClick={() => {
                      setQuery("");
                      setChosen(null);
                    }}
                  >
                    Change
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="min-w-0 flex-[1_1_220px] space-y-2">
                  <Label htmlFor="log-h">Hours</Label>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_HOURS.map((h) => (
                      <Button
                        key={h}
                        type="button"
                        size="sm"
                        variant={check.hours === h ? "default" : "outline"}
                        aria-pressed={check.hours === h}
                        className={cn(TOUCH, "min-w-12 font-semibold")}
                        onClick={() => setHours(String(h))}
                      >
                        {quickHoursLabel(h)}
                      </Button>
                    ))}
                  </div>
                  <Input
                    id="log-h"
                    inputMode="decimal"
                    autoComplete="off"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    placeholder="Or type, e.g. 1.75"
                    aria-describedby={messages.length > 0 ? MESSAGES_ID : undefined}
                    className={TOUCH}
                  />
                </div>
                <div className="min-w-0 flex-[1_1_160px] space-y-2">
                  <Label htmlFor="log-d">Date</Label>
                  <DatePicker id="log-d" value={date} onChange={setDate} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="log-c">
                  Comment{" "}
                  <span
                    className={cn(
                      "text-xs",
                      commentMissing ? "text-danger" : "font-normal text-muted-foreground",
                    )}
                  >
                    {check.commentRequired ? "(required)" : "(optional)"}
                  </span>
                </Label>
                <Textarea
                  id="log-c"
                  rows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="What did you work on?"
                  aria-required={check.commentRequired}
                  aria-invalid={commentMissing}
                  aria-describedby={messages.length > 0 ? MESSAGES_ID : undefined}
                  className={cn(commentMissing && "border-danger")}
                />
              </div>

              {loadError && (
                <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger">
                  {loadError.message}
                </p>
              )}

              {/* The rule messages say what to fix; the hours and comment fields point at them. */}
              <div id={MESSAGES_ID} className="space-y-2 empty:hidden">
                {messages.map((m) => (
                  <div
                    key={m.code}
                    role="status"
                    className={cn(
                      "rounded-md px-3 py-2 text-sm font-semibold",
                      m.tone === "danger"
                        ? "bg-danger-bg text-danger"
                        : "bg-warning-bg text-warning",
                    )}
                  >
                    {m.text}
                  </div>
                ))}
              </div>
            </DialogBody>

            <DialogFooter className="gap-2">
              {!editing && (
                <Button
                  type="button"
                  variant="outline"
                  className={TOUCH}
                  disabled={!canSave}
                  onClick={() => save(true)}
                >
                  Save and log another
                </Button>
              )}
              <Button
                type="button"
                className={TOUCH}
                disabled={!canSave}
                onClick={() => save(false)}
              >
                Save
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
